import { useState, useCallback, useRef } from 'react';

/**
 * 生成全局唯一的幂等键 (Idempotency Key)
 * 格式: [prefix_]timestamp_randomString
 *
 * @param prefix 可选前缀 (如 'plan', 'point', 'unlock')
 */
export function generateIdempotencyKey(prefix?: string): string {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 10);
  const baseKey = `${timestamp}_${randomStr}`;
  return prefix ? `${prefix}_${baseKey}` : baseKey;
}

export interface UseIdempotencyKeyOptions {
  /** 幂等键的前缀，如 'plan', 'point', 'unlock' */
  prefix?: string;
  /** 是否在挂载或初始化时自动生成 key，默认 true */
  autoGenerate?: boolean;
}

/**
 * 管理与生成 idempotency_key 的自定义 Hook
 */
export function useIdempotencyKey(options: UseIdempotencyKeyOptions = {}) {
  const { prefix = '', autoGenerate = true } = options;

  const [key, setKey] = useState<string>(() => {
    return autoGenerate ? generateIdempotencyKey(prefix) : '';
  });

  const keyRef = useRef(key);
  keyRef.current = key;

  /**
   * 刷新并生成一个新的 idempotency_key，返回新生成的 key
   */
  const refreshKey = useCallback((customPrefix?: string): string => {
    const newKey = generateIdempotencyKey(customPrefix ?? prefix);
    setKey(newKey);
    return newKey;
  }, [prefix]);

  /**
   * 获取当前 idempotency_key，若当前为空则立即生成一个
   */
  const getKey = useCallback((customPrefix?: string): string => {
    if (keyRef.current) {
      return keyRef.current;
    }
    return refreshKey(customPrefix);
  }, [refreshKey]);

  /**
   * 包装异步调用：自动传入生成的 idempotency_key，并在完成后/失败时自动刷新 key
   * 保证每次请求使用全新的幂等键，提升重复调用时的稳定性
   */
  const executeWithKey = useCallback(async <T>(
    action: (idempotencyKey: string) => Promise<T>,
    customPrefix?: string,
    autoRefreshAfter = true
  ): Promise<T> => {
    const currentKey = getKey(customPrefix);
    try {
      const result = await action(currentKey);
      if (autoRefreshAfter) {
        refreshKey(customPrefix);
      }
      return result;
    } catch (error) {
      refreshKey(customPrefix);
      throw error;
    }
  }, [getKey, refreshKey]);

  return {
    /** 当前幂等键 */
    key,
    /** 获取当前或最新生成的幂等键 */
    getKey,
    /** 手动刷新幂等键 */
    refreshKey,
    /** 独立生成幂等键的辅助方法 */
    generateKey: generateIdempotencyKey,
    /** 包装带幂等键请求的高阶执行函数 */
    executeWithKey,
  };
}
